import { useState, useEffect, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";

import useSharedStyles from "../../styles/Shared";
import useButtonStyles from "../../styles/Buttons";
import usePopoverStyles from "../../styles/Popovers";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { COLORS } from "../../themes/Base";
import Typography from "@mui/material/Typography";
import { getLocalTime } from "../../utils/Time";
import CopyIcon from "../../components/Icons/CopyIcon";
import CopyAllIcon from "../../components/Icons/CopyAllIcon";
import Popover from "@mui/material/Popover";
import { ListItemButton, Divider, List } from "@mui/material";
import { notify, notifySimple } from "../../themes/components/Notifications";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "../../components/Icons/DeleteIcon";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import ConfirmationDialog from "../../themes/components/ConfirmationDialog";
import { deleteSupplier } from "../../api/Supplier";
import { isEmpty } from "lodash";
import { stripQueryParams, copyToClipboard } from "../../utils/Formatters";

type Props = {
  supplier: Supplier;
  refetch: () => void;
};

const SupplierView: React.FC<Props> = ({ supplier, refetch }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [actionDelete, setActionDelete] = useState<boolean>(false);

  const navigate = useNavigate();
  const { classes, cx } = useSharedStyles();
  const supplierFieldNames = supplier.fields.map((f) => f.name);

  const onDeleteSubmit = async () => {
    try {
      const response = await deleteSupplier(supplier.id);
      const { messages, result } = response.supplierDelete;

      if (!isEmpty(messages)) {
        notify(messages);
      } else if (result) {
        notifySimple(["Supplier deleted"], "success");
        refetch();
      }
    } catch (err) {
      notifySimple(["Error deleting Supplier"], "error");
    }
  };

  const generateEndpointBox = (label: string, value: string) => (
    <Box
      key={label}
      className={classes.urlBox}
      sx={{
        mb: 1,
        justifyContent: "space-between",
      }}
    >
      <Box>
        <Box sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>{label}</Box>
        <Box sx={{ flex: 1, wordBreak: "break-all" }}>{value}</Box>
      </Box>
      <IconButton
        onClick={() => {
          const cleaned = stripQueryParams(value, supplierFieldNames);
          copyToClipboard(`${label}: ${cleaned}`, `<strong>${label}</strong>: ${cleaned}`);
        }}
      >
        <CopyIcon />
      </IconButton>
    </Box>
  );

  return (
    <>
      <Box className={classes.stickyHeader}>
        <Box height="44px" display="flex" mb="16px" alignItems="center">
          <NavLink
            to="/suppliers"
            style={{
              display: "flex",
              alignItems: "center",
              color: "#67676C",
              fontSize: 16,
              textDecoration: "none",
            }}
            className="back-link"
          >
            <ArrowBackIcon sx={{ fontSize: 20, mr: 1 }} />
            Back to Suppliers
          </NavLink>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>{supplier.name}</Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                color: COLORS.textSecondary,
              }}
            >
              <Typography sx={{ fontSize: 14 }}>
                Created: {getLocalTime(supplier.inserted_at).format("DD/MM/YYYY HH:mm:ss")}
              </Typography>
              <Typography sx={{ fontSize: 14 }}>
                Updated: {getLocalTime(supplier.updated_at).format("DD/MM/YYYY HH:mm:ss")}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              onClick={(e) => {
                setAnchorEl(e.currentTarget);
              }}
              className={classes.settingsButton}
            >
              <MoreVertIcon sx={{ color: "#4F4F52" }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
      <Box m={4} mt={26}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box>
            <Box sx={{ fontSize: 16 }}>Survey Redirect Endpoints (For Survey Tool)</Box>
            <Box sx={{ color: "#67676C", fontSize: 13, mb: 1 }}>
              Place these links in your survey tool settings to handle participant outcomes:
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
            <IconButton
              sx={{
                color: "#67676C",
                px: 2,
                fontSize: 14,
                borderRadius: "8px",
                background: "#F8F8F9",
              }}
              onClick={() => {
                const allEndpoints = [
                  { label: "Complete", value: supplier.endpoints.completed },
                  { label: "Screened Out", value: supplier.endpoints.screened_out },
                  { label: "Quality Termination", value: supplier.endpoints.terminated },
                  { label: "Quota Full", value: supplier.endpoints.quota_full },
                ];
                const plainAll = allEndpoints
                  .map((e) => `${e.label}: ${stripQueryParams(e.value, supplierFieldNames)}`)
                  .join("\n");
                const htmlAll = allEndpoints
                  .map((e) => `<strong>${e.label}</strong>: ${stripQueryParams(e.value, supplierFieldNames)}`)
                  .join("<br>");

                void copyToClipboard(plainAll, htmlAll);
              }}
            >
              <CopyAllIcon /> Copy All
            </IconButton>
          </Box>
        </Box>
        {generateEndpointBox("Complete", supplier.endpoints.completed)}
        {generateEndpointBox("Screened Out", supplier.endpoints.screened_out)}
        {generateEndpointBox("Quality Termination", supplier.endpoints.terminated)}
        {generateEndpointBox("Quota Full", supplier.endpoints.quota_full)}

        <Box mt={4}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Fields
          </Typography>
          {supplier.fields.map((field) => (
            <Box
              key={field.id}
              sx={{
                border: "1px solid #E4E8D3",
                borderRadius: "8px",
                background: "#fff",
                pt: 2,
                px: 2,
              }}
            >
              <Box sx={{ display: "flex", fontSize: 14 }}>
                <Box sx={{ flex: 2 }}>Field Name</Box>
                <Box sx={{ flex: 2, ml: 2.5 }}>Field Value</Box>
                <Box sx={{ flex: 1, textAlign: "left" }}>Required Field</Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    flex: 2,
                    bgcolor: "#EAEAED",
                    borderRadius: "8px",
                    px: "12px",
                    py: "9px",
                    fontSize: 15,
                    color: "#85858B",
                  }}
                >
                  {field.name}
                </Box>
                <Box
                  sx={{
                    flex: 2,
                    bgcolor: "#EAEAED",
                    borderRadius: "8px",
                    px: "12px",
                    py: "9px",
                    ml: 2,
                    fontSize: 15,
                    color: "#85858B",
                  }}
                >
                  {field.value}
                </Box>
                <Box sx={{ flex: 1, textAlign: "left", fontSize: 15, p: "12px 9px", ml: 2.5 }}>
                  {field.required ? "Yes" : "No"}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableScrollLock
        PaperProps={{
          className: cx(classes.innerBox),
        }}
      >
        <List className={classes.dropdownMenu}>
          <ListItemButton
            onClick={() => {
              setAnchorEl(null);
              navigate(`/supplier/${supplier?.id}/edit`);
            }}
            className={classes.dropdownMenuItem}
          >
            <EditIcon /> Edit Supplier
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              setAnchorEl(null);
              navigate("/supplier/create", {
                state: { initialValues: supplier },
              });
            }}
            className={classes.dropdownMenuItem}
          >
            <FileCopyIcon /> Duplicate & Add Supplier
          </ListItemButton>
          <Divider />
          <ListItemButton
            onClick={() => {
              setAnchorEl(null);
              setActionDelete(true);
            }}
            className={cx(classes.dropdownMenuItem, "red")}
          >
            <DeleteIcon /> Delete Supplier
          </ListItemButton>
        </List>
      </Popover>
      <ConfirmationDialog
        show={actionDelete}
        title="Are you sure?"
        description="This supplier will be permanently deleted"
        okButton="Confirm"
        cancelButton="Cancel"
        okAction={() => {
          if (actionDelete) {
            void onDeleteSubmit();
          }
        }}
        closeAction={() => setActionDelete(false)}
      />
    </>
  );
};

export default SupplierView;
